import React, { useState, useEffect,useMemo } from "react";
import axios from "axios";
import {
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Legend,
  ComposedChart,
  Area,
  AreaChart,
} from "recharts";
import { useCurrencies } from "../../hooks/useCurrencies";
import { Link } from "react-router-dom";

// ============= SPARKLINE COMPONENT =============
const MiniSparkline = ({ data, color = "#8884d8" }) => {
  if (!data || data.length < 2) return null;

  return (
    <div className="h-12 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data}>
          <defs>
            <linearGradient id={`gradient-${color}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="price"
            stroke={color}
            fill={`url(#gradient-${color})`}
            isAnimationActive={false}
            dot={false}
            strokeWidth={1.5}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============= PRICE SNAPSHOT COMPONENT =============
const PriceSnapshot = ({ currentPrice, averagePrice, change24h, coinsIcon }) => {
  const priceChange = currentPrice - averagePrice;
  const changePercent = ((priceChange / averagePrice) * 100).toFixed(1);
  const isUp = priceChange >= 0;

  return (
    <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] rounded-lg sm:rounded-xl shadow p-3 sm:p-4 mb-6 border border-[var(--panel-outline-color)]">
      <div className="flex justify-between items-start mb-4">
        <div>
          <p className="text-xs sm:text-sm text-[var(--button2-bg-color)] uppercase tracking-wider font-semibold mb-1">
            Current Price
          </p>
          <div className="flex items-center gap-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-[var(--chart-text)]">
              {currentPrice.toLocaleString()}
            </h2>
            {coinsIcon && <img src={coinsIcon} alt="coin" className="w-5 h-5 sm:w-6 sm:h-6" />}
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs sm:text-sm text-[var(--button2-bg-color)] uppercase tracking-wider font-semibold mb-1">
            24h Change
          </p>
          <p
            className={`text-lg sm:text-xl font-bold ${
              change24h >= 0 ? "text-[var(--teal)]" : "text-[var(--red)]"
            }`}
          >
            {change24h >= 0 ? "+" : ""}
            {change24h.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[var(--panel-outline-color)]">
        <div>
          <p className="text-xs text-[var(--button2-bg-color)] mb-1">Average Price</p>
          <p className="font-semibold text-[var(--chart-text)]">
            {averagePrice.toLocaleString()}
          </p>
        </div>
        <div>
          <p className="text-xs text-[var(--button2-bg-color)] mb-1">Difference from Avg</p>
          <p
            className={`font-semibold ${
              isUp ? "text-[var(--teal)]" : "text-[var(--red)]"
            }`}
          >
            {isUp ? "+" : ""}
            {priceChange.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })} ({changePercent}%)
          </p>
        </div>
      </div>
    </div>
  );
};

 const timeRanges = [
    { key: "24h", label: "24h", hours: 24 },
    { key: "3d", label: "3d", hours: 72 },
    { key: "7d", label: "7d", hours: 168 },
    { key: "15d", label: "15d", hours: 360 },
  ];

  // ============= TIME-RANGE FILTERED PRICE TREND =============
const PriceTrendChartFiltered = ({ priceTrendData }) => {
  const [timeRange, setTimeRange] = useState("7d");
 
  // Filter data based on selected time range
  const filteredData = useMemo(() => {
    if (!priceTrendData.length) return [];

    const now = new Date();
    const rangeConfig = timeRanges.find((r) => r.key === timeRange);
    if (!rangeConfig) return priceTrendData;

    const cutoffTime = new Date(now.getTime() - rangeConfig.hours * 60 * 60 * 1000);

    return priceTrendData.filter((item) => {
      // Parse the timestamp from the item
      const itemDate = new Date(item.timestamp || item.date);
      return itemDate >= cutoffTime;
    });
  }, [priceTrendData, timeRange]);

  return (
    <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] rounded-lg sm:rounded-xl shadow p-3 sm:p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <h4 className="font-semibold text-base sm:text-lg">Price Trend</h4>
        <div className="flex gap-2">
          {timeRanges.map((range) => (
            <button
              key={range.key}
              onClick={() => setTimeRange(range.key)}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-all ${
                timeRange === range.key
                  ? "bg-[var(--teal)] text-white"
                  : "bg-[var(--panel-outline-color)] text-[var(--chart-text)] hover:opacity-80"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-[250px] sm:h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={filteredData}>
            <defs>
              <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis
              dataKey="date"
              tick={{ fill: "var(--chart-text)", fontSize: 10 }}
              tickFormatter={(value) => {
                if (typeof value === "string" && value.includes("T")) {
                  return value.split("T")[0];
                }
                if (value instanceof Date) {
                  return value.toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  });
                }
                return String(value).length > 10
                  ? String(value).substring(0, 10)
                  : value;
              }}
            />
            <YAxis yAxisId="left" tick={{ fill: "var(--chart-text)", fontSize: 10 }} domain={["auto", "auto"]} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: "var(--chart-text)", fontSize: 10 }} domain={[0, "auto"]} />
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] border border-[var(--panel-outline-color)] p-2 rounded shadow text-xs">
                      <div className="font-semibold">{data.date}</div>
                      <div>Price: {data.price.toLocaleString()}</div>
                      <div>Listings: {data.counts.toLocaleString()}</div>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="price"
              name="Price"
              stroke="#8884d8"
              fill="url(#priceGradient)"
              strokeWidth={2}
              dot={false}
              isAnimationActive={true}
              animationDuration={300}
            />
            <Bar
              yAxisId="right"
              dataKey="counts"
              name="Listings"
              fill="var(--teal)"
              opacity={0.7}
              barSize={20}
              animationDuration={300}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// ============= ENHANCED SUMMARY CARDS WITH SPARKLINES =============
const SummaryCardWithSparkline = ({
  range,
  value,
  percent,
  sparklineData,
  color = "#8884d8",
}) => {
  // Handle Infinity and NaN values, round to 2 decimal places
  let displayPercent = percent;
  if (!isFinite(percent) || isNaN(percent)) {
    displayPercent = 0;
  } else {
    displayPercent = parseFloat(percent).toFixed(2);
  }

  return (
    <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] rounded-lg sm:rounded-xl shadow p-3 sm:p-4 flex flex-col h-full">
      <div className="mb-3">
        <p className="text-xs sm:text-sm text-[var(--button2-bg-color)] uppercase tracking-wider font-semibold mb-1">
          {range.toUpperCase()}
        </p>
        <p className="text-xl sm:text-2xl font-bold">{value}</p>
        <p
          className={`text-sm font-medium ${
            displayPercent >= 0 ? "text-green-500" : "text-red-500"
          }`}
        >
          {displayPercent >= 0 ? "+" : ""}
          {displayPercent}%
        </p>
      </div>
      <div className="flex-grow">
        <MiniSparkline data={sparklineData} color={color} />
      </div>
    </div>
  );
};

// ============= CANDLESTICK-STYLE MONTHLY CHART =============
const MonthlyRangeChart = ({ monthlyStats }) => {
  if (!monthlyStats || monthlyStats.length === 0) return null;

  const chartData = monthlyStats.map((month) => {
    const avg = (month.highestPrice + month.lowestPrice) / 2;
    return {
      name: month.month,
      High: month.highestPrice,
      Low: month.lowestPrice,
      Average: avg,
    };
  });

  return (
    <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] rounded-lg sm:rounded-xl shadow p-3 sm:p-4 mb-4">
      <h4 className="font-semibold text-lg mb-2">Monthly Price Ranges</h4>

      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={chartData}>
          <XAxis
            dataKey="name"
            tick={{ fill: "var(--chart-text)", fontSize: 10 }}
          />
          <YAxis tick={{ fill: "var(--chart-text)", fontSize: 10 }} />
          <Tooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                return (
                  <div className="bg-[var(--chart-bg)] text-[var(--chart-text)] outline outline-1 outline-[var(--panel-outline-color)] p-2 rounded shadow text-xs">
                    <div className="font-semibold mb-1">{data.name}</div>
                    <div className="text-green-500">
                      High: {data.High.toLocaleString()}
                    </div>
                    <div className="text-red-500">
                      Low: {data.Low.toLocaleString()}
                    </div>
                    <div className="text-blue-400">
                      Avg: {data.Average.toLocaleString(undefined, {
                        maximumFractionDigits: 0,
                      })}
                    </div>
                  </div>
                );
              }
              return null;
            }}
          />
          <Legend />
          <Bar dataKey="High" name="High" fill="#34d399" radius={[4, 4, 0, 0]} barSize={20} />
          <Bar dataKey="Low" name="Low" fill="#f87171" radius={[4, 4, 0, 0]} barSize={20} />
          <Line
            type="monotone"
            dataKey="Average"
            name="Average"
            stroke="#60a5fa"
            strokeWidth={2}
            dot={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============= ENHANCED LISTINGS TABLE =============
const EnhancedListingsTable = ({ marketListings, coinsIcon }) => {
  const [sortBy, setSortBy] = useState("date"); // "price" or "date"
  const [sortOrder, setSortOrder] = useState("desc");

  if (!marketListings || marketListings.listings.length === 0) {
    return (
      <div className="wiki-crafting text-center py-4">
        No listings available
      </div>
    );
  }

  // Determine which listings are recent (last hour)
  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

  // Sort listings
  const sortedListings = [...marketListings.listings].sort((a, b) => {
    let compareValue = 0;

    if (sortBy === "price") {
      compareValue = a.price - b.price;
    } else if (sortBy === "date") {
      compareValue = new Date(b.createdAt) - new Date(a.createdAt);
    }

    return sortOrder === "asc" ? compareValue : -compareValue;
  });

  return (
    <div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <button
          onClick={() => {
            setSortBy("price");
            setSortOrder(sortBy === "price" && sortOrder === "asc" ? "desc" : "asc");
          }}
          className={`text-xs px-2 py-1 rounded transition-all ${
            sortBy === "price"
              ? "bg-[var(--teal)] text-white"
              : "bg-[var(--panel-outline-color)] text-[var(--chart-text)] hover:opacity-80"
          }`}
        >
          Sort by Price {sortBy === "price" && (sortOrder === "asc" ? "↑" : "↓")}
        </button>
        <button
          onClick={() => {
            setSortBy("date");
            setSortOrder(sortBy === "date" && sortOrder === "desc" ? "asc" : "desc");
          }}
          className={`text-xs px-2 py-1 rounded transition-all ${
            sortBy === "date"
              ? "bg-[var(--teal)] text-white"
              : "bg-[var(--panel-outline-color)] text-[var(--chart-text)] hover:opacity-80"
          }`}
        >
          Sort by Date {sortBy === "date" && (sortOrder === "desc" ? "↓" : "↑")}
        </button>
      </div>

      <div className="overflow-auto max-h-[600px]">
        <table className="min-w-full market-table">
          <thead>
            <tr>
              <th className="px-2 text-left">Date/Time</th>
              <th className="px-2">Seller</th>
              <th className="px-2">Quantity</th>
              <th className="px-2">Market Fee</th>
              <th className="px-2">Price</th>
              <th className="px-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {sortedListings.map((p) => {
              const createdDate = new Date(p.createdAt);
              const isRecent = createdDate >= oneHourAgo;

              return (
                <tr
                  key={p._id}
                  className={isRecent ? "bg-green-500 bg-opacity-10" : ""}
                >
                  <td className="px-2">
                    {createdDate.toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                    {isRecent && (
                      <div className="text-xs text-green-400 font-semibold">
                        RECENT
                      </div>
                    )}
                  </td>
                  <td className="px-2">
                    <Link
                      to={`/lookup/players/${p.ownerId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="modal-link"
                    >
                      {marketListings.ownerUsernames[p.ownerId]}
                    </Link>
                  </td>
                  <td className="px-2">
                    {(p.quantity - p.purchasedQuantity).toLocaleString()}
                  </td>
                  <td className="px-2">{`${(p.fee * 100).toLocaleString()}%`}</td>
                  <td className="px-2">
                    {p.price.toLocaleString()} <img src={coinsIcon} alt="coin" />
                  </td>
                  <td className="px-2">
                    {(
                      p.price *
                      (p.quantity - p.purchasedQuantity)
                    ).toLocaleString()}{" "}
                    <img src={coinsIcon} alt="coin" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============= MAIN COMPONENT =============
export function ItemMarket({ item }) {
  const [marketPrices, setMarketPrices] = useState(null);
  const [marketHistory, setMarketHistory] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const [marketListings, setMarketListings] = useState(null);
  const [coinsIcon, setCoinsIcon] = useState(null);
  const { data } = useCurrencies();

  useEffect(() => {
    if (!data) return;

    const coin = data.find((cur) => cur.id === "cur_coins");
    if (coin) {
      setCoinsIcon(coin.image);
    }
  }, [data]);

  useEffect(() => {
    const fetchData = async (currentPriceData) => {
      try {
        const response = await axios.get(
          `https://api.pixelore.wiki/api/items/${item.id}`
        );
        if (response.data) {
          const updatedPrices = [...response.data.item[0].prices, currentPriceData];
          setMarketPrices(updatedPrices);
          setMonthlyStats(response.data.item[0].monthlyStats);
          setMarketListings(response.data.marketListings);
          setMarketHistory(item.history);
        }
      } catch (error) {
        console.error("Error fetching market data:", error);
      }
    };

    const currentTimestamp = new Date().toISOString();

    const currentPriceData = {
      price: item.baseMarketPrice,
      counts: item.marketPieces,
      timestamp: currentTimestamp,
      date: new Date().toISOString().split("T")[0], // Add date field for filtering
    };

    if (!item.marketPrices) {
      fetchData(currentPriceData);
      setMarketHistory(item.history);
    } else {
      const updatedPrices = [...item.marketPrices.prices, currentPriceData];
      setMarketPrices(updatedPrices);
      setMonthlyStats(item.marketPrices.monthlyStats);
      setMarketListings(item.marketPrices.marketListings);
    }
  }, [item]);

  if (!marketPrices || marketPrices.length <= 1) {
    return (
      <div className="wiki-crafting text-center py-8 text-[var(--chart-text)]">
        <p>No market data to display...</p>
      </div>
    );
  }

  // Prepare sparkline data for summary cards
  const summaryRanges = ["1h", "1d", "7d", "15d"];
  const summaryData = summaryRanges.map((range) => {
    // Get data for the past period
    const now = new Date();
    let hoursBack = 1;
    if (range === "1d") hoursBack = 24;
    if (range === "7d") hoursBack = 168;
    if (range === "15d") hoursBack = 360;

    const cutoffTime = new Date(now.getTime() - hoursBack * 60 * 60 * 1000);
    const periodData = marketPrices.filter((p) => {
      const date = new Date(p.timestamp);
      return date >= cutoffTime;
    });

    return {
      range,
      value: marketHistory?.[`qty${range}`]?.toLocaleString() ?? "N/A",
      percent: marketHistory?.[`change${range}`] ?? 0,
      sparklineData: periodData.map((p) => ({
        price: p.price,
        timestamp: p.timestamp,
      })),
    };
  });

  // Prepare price trend data
  const priceTrendData = marketPrices.map((p) => {
    const date = new Date(p.timestamp);
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();

    return {
      date: `${month}-${day}`,
      timestamp: p.timestamp, // Keep for filtering
      price: p.price,
      counts: p.counts,
    };
  });

  // Calculate current and average price
  const currentPrice = marketPrices[marketPrices.length - 1]?.price || 0;
  const averagePrice =
    marketPrices.reduce((sum, p) => sum + p.price, 0) / marketPrices.length;

  return (
    <div className="wiki-crafting">
      {/* PRICE SNAPSHOT - Leading section */}
      <PriceSnapshot
        currentPrice={currentPrice}
        averagePrice={averagePrice}
        change24h={marketHistory?.change1d ?? 0}
        coinsIcon={coinsIcon}
      />

      {/* PRICE TREND WITH TIME RANGE FILTERING */}
      <PriceTrendChartFiltered priceTrendData={priceTrendData} />

      {/* SUMMARY CARDS WITH SPARKLINES */}
      <div>
        <h4 className="font-semibold text-base sm:text-lg mb-3">Market Activity (Difference in Listings by Timeframe)</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4">
          {summaryData.map(({ range, value, percent, sparklineData }) => (
            <SummaryCardWithSparkline
              key={range}
              range={range}
              value={value}
              percent={percent}
              sparklineData={sparklineData}
              color={percent >= 0 ? "#58978e" : "#ff0202"}
            />
          ))}
        </div>
      </div>

      {/* MONTHLY RANGES WITH CANDLESTICK-STYLE AND AVERAGE LINE */}
      <MonthlyRangeChart monthlyStats={monthlyStats} />

      {/* ENHANCED LISTINGS TABLE WITH SORTING AND RECENT HIGHLIGHTING */}
      <EnhancedListingsTable
        marketListings={marketListings}
        coinsIcon={coinsIcon}
      />
    </div>
  );
}